import { Grid } from '@mui/material';
import React from 'react';
import { match } from 'ts-pattern';
import {
  useEffectiveRoleViewAuthorizationServiceContext,
  useGlobalLocaleContext,
} from '../../../context/contexts';
import { ChartComponentProps } from '../charts/chart-types';
import {
  KpiV2ComponentConfig,
  KpiV2DoubleMetricOptions,
  KpiV2SingleMetricOptions,
  KpiV2Type,
  LanguageString,
} from '../dashboards/types';
import { MetricQueryTooltip } from '../modals/MetricQueryModal';
import { KpiUi } from './kpi-ui/KpiUi';
import {
  useKpiV2SingleColumnValues,
  useKpiV2TwoColumnValues,
} from './kpi-v2-hooks';
import { KpiQueryMetaData } from './types';

type KpiV2Props = ChartComponentProps<
  KpiV2ComponentConfig,
  | 'manualDataRefetchCountHandle'
  | 'timeSliderState'
  | 'filtersState'
  | 'segmentationState'
>;

type KpiV2SingleColumnProps = Omit<KpiV2Props, 'componentConfig'> & {
  options: KpiV2SingleMetricOptions;
  title?: LanguageString;
  subtitle?: LanguageString;
  footer?: LanguageString;
};

type KpiV2DoubleColumnProps = Omit<KpiV2Props, 'componentConfig'> & {
  options: KpiV2DoubleMetricOptions;
  title?: LanguageString;
  subtitle?: LanguageString;
  footer?: LanguageString;
};

interface BaseKpiV2Props {
  meta: KpiQueryMetaData[];
  Component: React.ReactNode;
}

const BaseKpiV2 = (props: BaseKpiV2Props) => {
  const { meta, Component } = props;
  const metrics = meta.map((m) => m.metric);
  const metricsSql = meta.map((m) => m.metricsSql);
  const authorizationService =
    useEffectiveRoleViewAuthorizationServiceContext();
  const canSeeChartDevOptions = authorizationService.canSeeChartDevOptions();
  return (
    <Grid item container sx={{ position: 'relative', height: '100%' }}>
      {canSeeChartDevOptions ? (
        <Grid item sx={{ position: 'absolute', top: 0, right: 0, padding: 1 }}>
          <MetricQueryTooltip metrics={metrics} metricsSql={metricsSql} />
        </Grid>
      ) : null}
      {Component}
    </Grid>
  );
};

const KpiV2SingleColumn = (props: KpiV2SingleColumnProps) => {
  const { options, title, subtitle, footer, ...rest } = props;
  const locale = useGlobalLocaleContext();
  const { manualDataRefetchCountHandle, timeSliderState, filtersState } = rest;
  const config = {
    title,
    subtitle,
    footer,
    ...options,
  };
  const { meta, uiOptions } = useKpiV2SingleColumnValues(
    config,
    manualDataRefetchCountHandle,
    timeSliderState,
    filtersState,
    locale.selected
  );
  return <BaseKpiV2 meta={meta} Component={<KpiUi options={uiOptions} />} />;
};

const KpiV2DoubleColumn = (props: KpiV2DoubleColumnProps) => {
  const { options, title, subtitle, footer, ...rest } = props;
  const locale = useGlobalLocaleContext();
  const { manualDataRefetchCountHandle, timeSliderState, filtersState } = rest;
  const config = {
    title,
    subtitle,
    footer,
    ...options,
  };
  const { meta, uiOptions } = useKpiV2TwoColumnValues(
    config,
    manualDataRefetchCountHandle,
    timeSliderState,
    filtersState,
    locale.selected
  );
  return <BaseKpiV2 meta={meta} Component={<KpiUi options={uiOptions} />} />;
};

export const KpiV2 = (props: KpiV2Props) => {
  const { componentConfig } = props;
  return match(componentConfig)
    .with(
      { options: { kpiType: KpiV2Type.SINGLE_METRIC } },
      (componentConfig) => <KpiV2SingleColumn {...componentConfig} {...props} />
    )
    .with(
      { options: { kpiType: KpiV2Type.DOUBLE_METRIC } },
      (componentConfig) => <KpiV2DoubleColumn {...componentConfig} {...props} />
    )
    .exhaustive();
};
